import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useZaps } from '@/hooks/useZaps';
import { useWallet } from '@/hooks/useWallet';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { WalletModal } from '@/components/WalletModal';
import { Zap, Loader2, Sparkles, Copy, CheckCircle2, Rocket } from 'lucide-react';
import type { Event } from 'nostr-tools';
import QRCode from 'qrcode';
import { useQueryClient } from '@tanstack/react-query';
import confetti from 'canvas-confetti';

interface QuickZapButtonProps {
  target: Event;
  onZapSuccess?: () => void;
  className?: string;
  children?: React.ReactNode;
}

export function QuickZapButton({ target, onZapSuccess, className, children }: QuickZapButtonProps) {
  const { user } = useCurrentUser();
  const { webln, activeNWC } = useWallet();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('21000');
  const [comment, setComment] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  const { zap, isZapping, invoice, resetInvoice } = useZaps(
    target,
    webln,
    activeNWC,
    () => {
      setShowSuccess(true);
      
      // Fire confetti!
      const count = 200;
      const defaults = {
        origin: { y: 0.7 },
        colors: ['#FF6B00', '#FF8533', '#FFA366', '#FFCC00', '#FFD700'],
      };

      function fire(particleRatio: number, opts: confetti.Options) {
        confetti({
          ...defaults,
          ...opts,
          particleCount: Math.floor(count * particleRatio),
        });
      }

      fire(0.25, {
        spread: 26,
        startVelocity: 55,
      });

      fire(0.2, {
        spread: 60,
      });

      fire(0.35, {
        spread: 100,
        decay: 0.91,
        scalar: 0.8,
      });

      fire(0.1, {
        spread: 120,
        startVelocity: 25,
        decay: 0.92,
        scalar: 1.2,
      });

      fire(0.1, {
        spread: 120,
        startVelocity: 45,
      });
      
      // Invalidate all progress queries to update UI everywhere
      queryClient.invalidateQueries({ queryKey: ['fundraiser-progress'] });
      queryClient.invalidateQueries({ queryKey: ['zaps'] });
      
      setTimeout(() => {
        setOpen(false);
        setShowSuccess(false);
        setAmount('21000');
        setComment('');
        onZapSuccess?.();
      }, 3000);
    }
  );

  // Generate QR code when invoice is available
  useEffect(() => {
    if (invoice) {
      QRCode.toDataURL(invoice, { 
        width: 350,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' }
      }).then(setQrCodeUrl).catch(console.error);
    } else {
      setQrCodeUrl('');
    }
  }, [invoice]);

  const handleZap = async () => {
    const sats = parseInt(amount);
    if (isNaN(sats) || sats <= 0) return;

    await zap(sats, comment);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      resetInvoice();
      setComment('');
      setShowSuccess(false);
      setCopied(false);
    }
  };

  const handleCopyInvoice = async () => {
    if (invoice) {
      await navigator.clipboard.writeText(invoice);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const quickAmounts = [1000, 5000, 10000, 21000, 50000, 100000];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children || (
          <Button className={className}>
            <Zap className="h-5 w-5 mr-2" fill="currentColor" />
            Support Now
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg bg-white border-2 border-orange-500/20 shadow-2xl">
        <DialogHeader>
          <DialogTitle className="text-3xl font-black text-black">Support This Campaign</DialogTitle>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {showSuccess ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              className="py-20 text-center"
            >
              <motion.div
                animate={{ 
                  rotate: [0, 360],
                  scale: [1, 1.4, 1.2],
                  y: [0, -20, 0]
                }}
                transition={{ 
                  duration: 1,
                  ease: 'easeOut'
                }}
              >
                <Rocket className="h-32 w-32 text-orange-500 mx-auto mb-6 drop-shadow-2xl" />
              </motion.div>
              <motion.h3 
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-5xl font-black text-black mb-4"
              >
                Zap Sent! 🎉
              </motion.h3>
              <motion.p 
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-2xl text-orange-600 font-bold"
              >
                Thank you for your support!
              </motion.p>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.7, type: 'spring', stiffness: 200 }}
                className="mt-6"
              >
                <div className="inline-flex items-center gap-2 bg-orange-50 px-6 py-3 rounded-full border-2 border-orange-500/30">
                  <Sparkles className="h-5 w-5 text-orange-500" />
                  <span className="text-base font-bold text-black">Campaign updated!</span>
                </div>
              </motion.div>
            </motion.div>
          ) : invoice ? (
            <motion.div
              key="invoice"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="bg-white p-6 rounded-2xl border-2 border-orange-500/20">
                {qrCodeUrl && (
                  <motion.img 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                    src={qrCodeUrl} 
                    alt="Lightning Invoice QR Code" 
                    className="w-full max-w-[350px] mx-auto rounded-xl" 
                  />
                )}
              </div>
              
              <div className="space-y-3">
                <Label className="text-black font-black text-lg">Lightning Invoice</Label>
                <Textarea
                  value={invoice}
                  readOnly
                  rows={4}
                  className="font-mono text-xs bg-orange-50/50 border-2 border-orange-500/30 text-black"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleCopyInvoice}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold h-14"
                >
                  {copied ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-5 w-5 mr-2" />
                      Copy Invoice
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={resetInvoice}
                  className="flex-1 border-2 border-orange-500 text-orange-600 hover:bg-orange-50 font-bold h-14"
                >
                  Back
                </Button>
              </div>

              <p className="text-sm text-center text-gray-600 font-medium">
                Scan the QR code or copy the invoice to pay with any Lightning wallet
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-6"
            >
              {/* Wallet Status */}
              {user && !activeNWC && !webln && (
                <WalletModal>
                  <Button variant="outline" className="w-full border-2 border-orange-500 text-orange-600 hover:bg-orange-50 font-bold h-14">
                    ⚡ Connect Wallet for Instant Payments
                  </Button>
                </WalletModal>
              )}

              {/* Quick Amounts */}
              <div className="space-y-4">
                <Label className="text-black font-black text-xl">Select Amount</Label>
                <div className="grid grid-cols-3 gap-3">
                  {quickAmounts.map(amt => (
                    <Button
                      key={amt}
                      type="button"
                      variant={amount === amt.toString() ? 'default' : 'outline'}
                      onClick={() => setAmount(amt.toString())}
                      className={amount === amt.toString() 
                        ? 'bg-orange-500 text-white font-bold border-2 border-orange-600 h-14 text-lg shadow-lg'
                        : 'border-2 border-orange-500/30 text-orange-600 hover:bg-orange-50 font-bold h-14 text-lg'
                      }
                    >
                      {(amt / 1000).toLocaleString()}k
                    </Button>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div className="space-y-3">
                <Label htmlFor="amount" className="text-black font-black text-xl">
                  Custom Amount (sats)
                </Label>
                <Input
                  id="amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="bg-orange-50/50 border-2 border-orange-500/30 text-black text-3xl font-black h-16 focus:ring-4 focus:ring-orange-500/20 text-center"
                />
              </div>

              {/* Comment */}
              {user && (
                <div className="space-y-3">
                  <Label htmlFor="comment" className="text-black font-bold text-lg">
                    Add a Message (Optional)
                  </Label>
                  <Textarea
                    id="comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Your message of support..."
                    rows={3}
                    className="bg-orange-50/50 border-2 border-orange-500/30 text-black resize-none text-base font-medium"
                  />
                </div>
              )}

              {/* Send Button */}
              <Button
                onClick={handleZap}
                disabled={isZapping || parseInt(amount) <= 0}
                className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white h-20 text-2xl font-black shadow-2xl shadow-orange-500/40"
              >
                {isZapping ? (
                  <>
                    <Loader2 className="mr-3 h-7 w-7 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Zap className="mr-3 h-7 w-7" fill="currentColor" />
                    Zap {parseInt(amount).toLocaleString()} sats
                  </>
                )}
              </Button>

              {activeNWC && (
                <div className="bg-green-50 border-2 border-green-500/30 p-4 rounded-xl">
                  <p className="text-base text-center text-green-700 font-bold">
                    ⚡ Connected via Nostr Wallet Connect
                  </p>
                  <p className="text-sm text-center text-gray-600 mt-1">
                    Payment will be instant and automatic
                  </p>
                </div>
              )}
              {webln && !activeNWC && (
                <div className="bg-green-50 border-2 border-green-500/30 p-4 rounded-xl">
                  <p className="text-base text-center text-green-700 font-bold">
                    ⚡ Connected via WebLN Extension
                  </p>
                  <p className="text-sm text-center text-gray-600 mt-1">
                    One-click payment with your browser wallet
                  </p>
                </div>
              )}
              {!activeNWC && !webln && (
                <div className="bg-orange-50 border-2 border-orange-500/30 p-4 rounded-xl">
                  <p className="text-base text-center text-orange-700 font-bold">
                    📄 Invoice Payment
                  </p>
                  <p className="text-sm text-center text-gray-600 mt-1">
                    Scan QR code or copy invoice to pay
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
